import React, { useState, useEffect } from 'react';
import { ArrowLeft, Send, CheckCircle, AlertCircle, Users, ExternalLink, MessageSquare, Calendar, Clock, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import functions from components.js
import { 
  sendPetitionEventWebhook, 
  isGovernmentMinister, 
  canAddGovernmentResponse, 
  canEditDebateInfo, 
  canDeletePetitions, 
  canManageTerms,
  SignersList,
  GovernmentAdminPanel,
  getUserAvatar,
  isBritishCitizen,
  hasUserSignedPetition,
  getAllPetitions,
  signPetition,
  unsignPetition,
  getCurrentTerm,
  formatTermDisplay,
  getPetitionsByTerm,
  getParliamentaryTerms,
  canRemoveGovernmentResponse,
  canRemoveDebateInfo,
  GOVERNMENT_POSITIONS,
  deletePetition
} from './components';

// Import sendPetitionEventWebhook from components.js (no need for webhook URL here)

export const PetitionDetail = ({ petition, onBack, user, onSign }) => {
  const navigate = useNavigate();
  const [hasSignedPetition, setHasSignedPetition] = useState(false);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const [signatureCount, setSignatureCount] = useState(petition?.signatures || 0);
  const [signers, setSigners] = useState(petition?.signers || []);
  const [governmentResponse, setGovernmentResponse] = useState('');
  const [selectedGovernmentRole, setSelectedGovernmentRole] = useState('');
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [showDebateForm, setShowDebateForm] = useState(false);
  const [debateDate, setDebateDate] = useState('');
  const [debateLocation, setDebateLocation] = useState('Westminster Hall');
  const [debateStatus, setDebateStatus] = useState('scheduled');
  const [hansardLink, setHansardLink] = useState('');
  const [notDebatedReason, setNotDebatedReason] = useState('');

  // Check if user has signed this petition
  useEffect(() => {
    if (user && petition) {
      setHasSignedPetition(hasUserSignedPetition(user.id, petition));
      setSignatureCount(petition.signatures || 0);
      setSigners(petition.signers || []);
    }
  }, [user, petition]);

  // Listen for signature updates
  useEffect(() => {
    const handleSignatureUpdate = async () => {
      if (user && petition) {
        // Get updated petition data
        const allPetitions = await getAllPetitions();
        const updatedPetition = allPetitions.find(p => String(p.id) === String(petition.id));
        if (updatedPetition) {
          setHasSignedPetition(hasUserSignedPetition(user.id, updatedPetition));
          setSignatureCount(updatedPetition.signatures || 0);
          setSigners(updatedPetition.signers || []);
        }
      }
    };

    window.addEventListener('signaturesUpdated', handleSignatureUpdate);
    return () => window.removeEventListener('signaturesUpdated', handleSignatureUpdate);
  }, [user, petition]);

  // Handle signing/unsigning
  const handleSignature = async () => {
    if (!user) {
      alert('Please log in to sign this petition.');
      return;
    }

    if (!isBritishCitizen(user)) {
      alert('Only British Citizens can sign petitions.');
      return;
    }

    // Prevent double-clicking
    if (isSubmittingSignature) {
      return;
    }

    setIsSubmittingSignature(true);

    try {
      let result;
      if (hasSignedPetition) {
        result = await unsignPetition(petition.id, user);
      } else {
        result = await signPetition(petition.id, user);
      }

      if (result.success) {
        const newSignedState = !hasSignedPetition;
        setHasSignedPetition(newSignedState);
        setSignatureCount(result.newCount);
        
        // Fetch updated petition data to get the latest signers list
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}`);
          if (response.ok) {
            const updatedPetition = await response.json();
            setSigners(updatedPetition.signers || []);
            
            // Double-check the signed state based on actual signers list
            const userIsSigner = updatedPetition.signers.some(signer => String(signer.id) === String(user.id));
            setHasSignedPetition(userIsSigner);
          }
        } catch (fetchError) {
          console.error('Error fetching updated petition:', fetchError);
        }
        
        // Call parent callback if provided
        if (onSign) {
          onSign(petition.id, newSignedState);
        }
      } else {
        alert(result.error || 'Failed to process signature');
      }
    } catch (error) {
      console.error('Error handling signature:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  // Populate form when editing existing government response
  useEffect(() => {
    if (showResponseForm && petition.governmentResponse) {
      setGovernmentResponse(petition.governmentResponse);
      // Use the existing role from metadata if available, otherwise fallback to user's role
      if (petition.governmentResponseMeta && petition.governmentResponseMeta.addedByRole) {
        setSelectedGovernmentRole(petition.governmentResponseMeta.addedByRole);
      } else {
        setSelectedGovernmentRole('');
      }
    } else if (showResponseForm) {
      // Clear form when adding new response
      setGovernmentResponse('');
      setSelectedGovernmentRole('');
    }
  }, [showResponseForm, petition.governmentResponse, petition.governmentResponseMeta]);

  const handleRemoveGovernmentResponse = async () => {
    if (window.confirm('Are you sure you want to remove the government response? This action cannot be undone.')) {
      try {
        
        // Send Discord notification first
        sendPetitionEventWebhook('government_response_removed', petition, user, {
          removedResponse: petition.governmentResponse
        });

        // Call backend API to remove government response
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}/government-response`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error('Failed to remove government response');
        }

        // Update local petition data
        const updatedPetition = {
          ...petition,
          governmentResponse: null,
          governmentResponseMeta: null
        };
        Object.assign(petition, updatedPetition);
        
        alert('Government response removed successfully!');
        // Instead of reloading, dispatch an event to refresh data
        window.dispatchEvent(new CustomEvent('petitionsUpdated'));
      } catch (error) {
        console.error('Error removing government response:', error);
        alert('Failed to remove government response. Please try again.');
      }
    }
  };

  const handleDeletePetition = () => {
    if (!canDeletePetitions(user)) {
      alert('You do not have permission to delete petitions.');
      return;
    }

    const isConfirmed = window.confirm('Are you sure you want to delete this petition? This action cannot be undone.');
    
    if (isConfirmed) {
      try {
        // Use the proper deletePetition function
        const result = deletePetition(petition.id);
        
        if (result.success) {
          // Send Discord notification on successful deletion using secure function
          sendPetitionEventWebhook('petition_deleted', petition, user, {
            reason: 'Administrative action'
          }).catch(err => console.error('Discord notification error:', err));

          alert(result.message);
          onBack(); // Navigate back to petitions list
        } else {
          alert(result.message);
        }
      } catch (error) {
        console.error('Error deleting petition:', error);
        alert('Failed to delete petition. Please try again.');
      }
    }
  };

  const handleRemoveDebateInfo = async () => {
    if (window.confirm('Are you sure you want to remove the debate information? This action cannot be undone.')) {
      try {
        
        // Send Discord notification first
        sendPetitionEventWebhook('debate_removed', petition, user, {});

        // Call backend API to remove debate info
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}/debate-info`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error('Failed to remove debate information');
        }

        // Update local petition data
        const updatedPetition = {
          ...petition,
          debateInfo: null,
          debateInfoMeta: null
        };
        Object.assign(petition, updatedPetition);
        
        alert('Debate information removed successfully!');
        // Instead of reloading, dispatch an event to refresh data
        window.dispatchEvent(new CustomEvent('petitionsUpdated'));
      } catch (error) {
        console.error('Error removing debate information:', error);
        alert('Failed to remove debate information. Please try again.');
      }
    }
  };




  const [petitionTerm, setPetitionTerm] = useState(null);

  // Get the term associated with this petition
  useEffect(() => {
    const fetchPetitionTerm = async () => {
      if (petition && petition.termId) {
        try {
          const terms = await getParliamentaryTerms();
          const term = terms.find(t => String(t.id) === String(petition.termId));
          setPetitionTerm(term);
        } catch (error) {
          console.error('Error fetching petition term:', error);
        }
      }
    };
    
    fetchPetitionTerm();
  }, [petition]);

  const handleGovernmentResponse = async (e) => {
    e.preventDefault(); // Prevent form submission
    
    if (!governmentResponse.trim()) {
      alert('Please enter a government response');
      return;
    }

    if (!selectedGovernmentRole) {
      alert('Please select your government role');
      return;
    }

    try {
      // Send government response to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}/government-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          petition_id: petition.id,
          response_text: governmentResponse,
          added_by: user.displayName || user.username,
          added_by_role: selectedGovernmentRole
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add government response');
      }

      // Send Discord notification
      const isEdit = petition.governmentResponse && petition.governmentResponse !== governmentResponse;
      
      if (isEdit) {
        // Send edit webhook
        sendPetitionEventWebhook('government_response_edited', petition, user, {
          oldResponse: petition.governmentResponse,
          newResponse: governmentResponse,
          selectedRole: selectedGovernmentRole
        });
      } else {
        // Send add webhook
        sendPetitionEventWebhook('government_response_added', petition, user, {
          response: governmentResponse,
          selectedRole: selectedGovernmentRole
        });
      }

      // Clear form
      setGovernmentResponse('');
      setSelectedGovernmentRole('');
      setShowResponseForm(false);
      
      alert('Government response added successfully!');
      
      // Trigger petition update to refresh the display
      window.dispatchEvent(new CustomEvent('petitionsUpdated'));
      
    } catch (error) {
      console.error('Error adding government response:', error);
      alert('Failed to add government response. Please try again.');
    }
  };

  const handleDebateResponse = async (e) => {
    e?.preventDefault(); // Prevent form submission
    
    // Only require date for scheduled and debated petitions
    if ((debateStatus === 'scheduled' || debateStatus === 'debated') && !debateDate) {
      alert('Please enter a debate date');
      return;
    }

    // Validate required fields based on status
    if (debateStatus === 'debated' && !hansardLink.trim()) {
      alert('Please provide the Hansard link for the completed debate');
      return;
    }

    if (debateStatus === 'not_debated' && !notDebatedReason.trim()) {
      alert('Please provide a reason for not debating this petition');
      return;
    }

    if (debateStatus === 'cancelled' && !notDebatedReason.trim()) {
      alert('Please provide a reason for cancelling the debate');
      return;
    }

    // Prevent logout by not refreshing page
    try {
      // Create new debate info object
      const newDebateInfo = {
        date: (debateStatus === 'scheduled' || debateStatus === 'debated') ? debateDate : null,
        status: debateStatus,
        hansardLink: (debateStatus === 'debated' && hansardLink.trim()) ? hansardLink.trim() : null,
        notDebatedReason: ((debateStatus === 'not_debated' || debateStatus === 'cancelled') && notDebatedReason.trim()) ? notDebatedReason.trim() : null
      };

      // Save debate info to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}/debate-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          petition_id: petition.id,
          status: debateStatus,
          date: debateDate,
          location: debateLocation.trim() || 'Westminster Hall',
          hansard_url: (debateStatus === 'debated' && hansardLink.trim()) ? hansardLink.trim() : null,
          not_debated_reason: (debateStatus === 'not_debated' && notDebatedReason.trim()) ? notDebatedReason.trim() : null,
          added_by: user.displayName || user.username,
          added_by_role: user.userType || 'Government Official'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save debate information');
      }

      // Send Discord notification for debate event
      const isEdit = petition.debateInfo !== null;
      
      if (isEdit) {
        sendPetitionEventWebhook('debate_edited', petition, user, {
          date: debateDate,
          status: debateStatus,
          location: debateLocation.trim() || 'Westminster Hall',
          hansardLink: hansardLink.trim() || null,
          notDebatedReason: notDebatedReason.trim() || null
        });
      } else {
        sendPetitionEventWebhook('debate_added', petition, user, {
          date: debateDate,
          status: debateStatus,
          location: debateLocation.trim() || 'Westminster Hall',
          hansardLink: hansardLink.trim() || null,
          notDebatedReason: notDebatedReason.trim() || null
        });
      }

      alert(`Debate information ${debateStatus === 'debated' ? 'completion' : 'scheduling'} submitted successfully!`);
      
      // Reset form
      setDebateDate('');
      setDebateLocation('Westminster Hall');
      setDebateStatus('scheduled');
      setHansardLink('');
      setNotDebatedReason('');
      setShowDebateForm(false);
      
      // Trigger petition update to refresh the display
      window.dispatchEvent(new CustomEvent('petitionsUpdated'));
      
    } catch (error) {
      console.error('Error saving debate information:', error);
      alert('Failed to save debate information. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-600 text-white py-4 lg:py-6">
        <div className="container mx-auto px-4">
          <button
            onClick={onBack}
            className="text-blue-200 hover:text-white mb-2 text-sm lg:text-base"
          >
            ← Back to petitions
          </button>
          <div>
            {petitionTerm ? (
              <>
                <p className="text-blue-200 text-sm lg:text-base">This petition was submitted during {formatTermDisplay(petitionTerm)}</p>
                <p className="text-blue-200 text-sm lg:text-base">
                  <a 
                    href={`/petitions/term/${petitionTerm.id}`}
                    onClick={async (e) => {
                      e.preventDefault();
                      // Fetch petitions for this term
                      const termPetitions = await getPetitionsByTerm(petitionTerm.id);
                      if (termPetitions.length > 0) {
                        // Navigate to all petitions page with term filter
                        navigate(`/petitions?term=${petitionTerm.id}`);
                      } else {
                        alert(`No other petitions found for ${formatTermDisplay(petitionTerm)}`);
                      }
                    }}
                    className="text-blue-200 hover:text-white underline"
                  >
                    View other petitions from this parliamentary term
                  </a>
                </p>
              </>
            ) : (
              <p className="text-blue-200">Loading parliamentary term information...</p>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 lg:py-8 max-w-4xl">

        {/* Government Response Form */}
        {user && isGovernmentMinister(user) && (
          <div className="max-w-lg mx-auto mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-base lg:text-lg font-semibold mb-3 text-blue-800 flex items-center">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Government Admin Panel
            </h3>

            {/* Government Response Management */}
            <div className="mb-4">
              <h4 className="font-medium text-blue-800 mb-2">Government Response</h4>
              {petition.governmentResponse ? (
                <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm text-blue-600">
                      <strong>Current Response:</strong>
                      {petition.governmentResponseMeta && (
                        <div className="text-xs text-gray-600 mt-1">
                          Added by: <strong>{petition.governmentResponseMeta.addedBy}</strong>
                          <br />
                          Date: {new Date(petition.governmentResponseMeta.addedAt).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                          {petition.governmentResponseMeta.editedBy && (
                            <div className="mt-1">
                              Last edited by: <strong>{petition.governmentResponseMeta.editedBy}</strong> ({petition.governmentResponseMeta.editedByRole})
                              <br />
                              Date: {new Date(petition.governmentResponseMeta.editedAt).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {canAddGovernmentResponse(user) && (
                        <button
                          onClick={() => setShowResponseForm(!showResponseForm)}
                          className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700"
                        >
                          {petition.governmentResponse ? 'Edit' : 'Add'}
                        </button>
                      )}
                      {canRemoveGovernmentResponse(user) && (
                        <button
                          onClick={() => handleRemoveGovernmentResponse()}
                          className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">
                    {typeof petition.governmentResponse === 'object' 
                      ? petition.governmentResponse.text || petition.governmentResponse.content || JSON.stringify(petition.governmentResponse)
                      : petition.governmentResponse
                    }
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">No government response yet.</p>
                  {canAddGovernmentResponse(user) && (
                    <button
                      onClick={() => setShowResponseForm(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                    >
                      Add Government Response
                    </button>
                  )}
                </div>
              )}
              
            </div>

            {/* Debate Info Management */}
            <div className="mb-4">
              <h4 className="font-medium text-blue-800 mb-2">Parliament Debate</h4>
              {petition.debateInfo ? (
                <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm text-blue-600">
                      <strong>Current Debate Info:</strong>
                      {petition.debateInfoMeta && (
                        <div className="text-xs text-gray-600 mt-1">
                          Added by: <strong>{petition.debateInfoMeta.addedBy}</strong>
                          <br />
                          Date: {new Date(petition.debateInfoMeta.addedAt).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                          {petition.debateInfoMeta.editedBy && (
                            <div className="mt-1">
                              Last edited by: <strong>{petition.debateInfoMeta.editedBy}</strong> ({petition.debateInfoMeta.editedByRole})
                              <br />
                              Date: {new Date(petition.debateInfoMeta.editedAt).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {canEditDebateInfo(user) && (
                        <button
                          onClick={() => setShowDebateForm(true)}
                          className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700"
                        >
                          Edit
                        </button>
                      )}
                      {canRemoveDebateInfo(user) && (
                        <button
                          onClick={() => handleRemoveDebateInfo()}
                          className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">
                    {typeof petition.debateInfo === 'object' 
                      ? new Date(petition.debateInfo.date).toLocaleDateString()
                      : petition.debateInfo.substring(0, 100) + '...'
                    }
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">No debate scheduled yet.</p>
                  {canEditDebateInfo(user) && (
                    <button
                      onClick={() => setShowDebateForm(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm"
                    >
                      Schedule Debate
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Admin Actions */}
            <div className="space-y-2">
              {canDeletePetitions(user) && (
                <button
                  onClick={handleDeletePetition}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                  Delete Petition
                </button>
              )}

              {canManageTerms(user) && (
                <a
                  href="/term-management"
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-center block"
                  onClick={(e) => {
                    e.preventDefault();
                    // Use React Router navigation instead of window.location
                    window.location.pathname = '/term-management';
                  }}
                >
                  Manage Parliamentary Terms
                </a>
              )}
            </div>

            {showResponseForm && (
              <div className="mt-4 p-3 bg-green-100 rounded">
                <h4 className="font-semibold mb-2 text-green-800">
                  {petition.governmentResponse ? 'Update Government Response' : 'Add Government Response'}
                </h4>
                <form onSubmit={handleGovernmentResponse}>
                  <textarea
                    value={governmentResponse}
                    onChange={(e) => setGovernmentResponse(e.target.value)}
                    placeholder="Enter the government's response to this petition..."
                    className="w-full p-2 border border-green-300 rounded"
                    rows={4}
                    required
                  />
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-green-700 mb-1">
                      Select your government role *
                    </label>
                    <select
                      value={selectedGovernmentRole}
                      onChange={(e) => setSelectedGovernmentRole(e.target.value)}
                      className="w-full p-2 border border-green-300 rounded"
                      required
                    >
                      <option value="">-- Select your role --</option>
                      {GOVERNMENT_POSITIONS.map((position) => (
                        <option key={position} value={position}>
                          {position}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mt-2">
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700 mr-2"
                    >
                    Submit Government Response
                  </button>
                  <button
                    onClick={() => setShowResponseForm(false)}
                    className="bg-gray-500 text-white px-4 py-1 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  </div>
                </form>
              </div>
            )}

            {showDebateForm && (
              <div className="mt-4 p-3 bg-purple-100 rounded">
                <h4 className="font-semibold mb-2 text-purple-800">
                  {petition.debateInfo ? 'Update Parliament Debate' : 'Add Parliament Debate'}
                </h4>
                <form onSubmit={handleDebateResponse}>
                  <div className="space-y-4">
                    {(debateStatus === 'scheduled' || debateStatus === 'debated') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Debate Date *</label>
                        <input
                          type="date"
                          value={debateDate}
                          onChange={(e) => setDebateDate(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Debate Status *</label>
                      <select
                        value={debateStatus}
                        onChange={(e) => setDebateStatus(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        required
                      >
                        <option value="scheduled">Scheduled</option>
                        <option value="debated">Debated</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="not_debated">Not debated in parliament</option>
                      </select>
                    </div>
                    
                    {debateStatus === 'debated' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hansard PDF Link *</label>
                        <input
                          type="url"
                          value={hansardLink}
                          onChange={(e) => setHansardLink(e.target.value)}
                          placeholder="https://hansard.parliament.uk/..."
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Link to the official Hansard record of the parliamentary debate
                        </p>
                      </div>
                    )}

                    {debateStatus === 'cancelled' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason for cancellation *</label>
                        <textarea
                          value={notDebatedReason}
                          onChange={(e) => setNotDebatedReason(e.target.value)}
                          placeholder="Please provide the reason why this debate was cancelled..."
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          rows={3}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Explain why this parliamentary debate was cancelled
                        </p>
                      </div>
                    )}

                    {debateStatus === 'not_debated' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason for not debating *</label>
                        <textarea
                          value={notDebatedReason}
                          onChange={(e) => setNotDebatedReason(e.target.value)}
                          placeholder="Please provide the reason why this petition will not be debated in parliament..."
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          rows={3}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Explain why this petition will not receive a parliamentary debate
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowDebateForm(false)}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                    >
                      {debateStatus === 'debated' ? 'Record Completion' : debateStatus === 'scheduled' ? 'Schedule Debate' : 'Cancel Debate'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-4 lg:p-8">
          <div className="mb-6">
            <span className={`px-3 py-1 rounded-full text-xs lg:text-sm font-medium ${
              petition.status === 'open' ? 'bg-green-100 text-green-800' :
              petition.status === 'closed' ? 'bg-red-100 text-red-800' :
              petition.status === 'awaiting_government_response' ? 'bg-blue-100 text-blue-800' :
              petition.status === 'government_response' ? 'bg-blue-100 text-blue-800' :
              petition.status === 'awaiting_debate_in_parliament' ? 'bg-purple-100 text-purple-800' :
              petition.status === 'debated_in_parliament' ? 'bg-purple-100 text-purple-800' :
              petition.status === 'not_debated_in_parliament' ? 'bg-gray-100 text-gray-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {petition.status === 'awaiting_government_response' ? 'Awaiting Government Response' :
               petition.status === 'government_response' ? 'Government Response' :
               petition.status === 'awaiting_debate_in_parliament' ? 'Awaiting Debate in Parliament' :
               petition.status === 'debated_in_parliament' ? 'Debated in Parliament' :
               petition.status === 'not_debated_in_parliament' ? 'Not Debated in Parliament' :
               petition.status.charAt(0).toUpperCase() + petition.status.slice(1)}
            </span>
            
            {petition.status === 'closed' && (
              <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-red-800 font-medium">Petition Closed</p>
                    {petition.closureReason === 'term_ended' ? (
                      <p className="text-red-700 text-sm">This petition was automatically closed due to the parliamentary term ending.</p>
                    ) : petition.closureReason === 'three_weeks_insufficient_signatures' ? (
                      <p className="text-red-700 text-sm">This petition was automatically closed after 3 weeks due to insufficient signatures (minimum 10 required).</p>
                    ) : petition.signatures < 10 ? (
                      <p className="text-red-700 text-sm">This petition was automatically closed after 3 weeks due to insufficient signatures (minimum 10 required).</p>
                    ) : (
                      <p className="text-red-700 text-sm">This petition was automatically closed due to the parliamentary term ending.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">{petition.title}</h1>
          <p className="text-sm text-gray-600 mb-4">Created by {petition.creator || 'Anonymous'}</p>
          
          <div className="flex flex-wrap gap-2 mb-6">
            {petition.tags && petition.tags.map((tag, index) => (
              <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                {tag}
              </span>
            ))}
          </div>

          <div className="prose max-w-none mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">{petition.description}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 lg:p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-4 sm:gap-0">
              <div className="text-center sm:text-left">
                <div className="text-2xl lg:text-3xl font-bold text-blue-600">
                  {signatureCount.toLocaleString()}
                </div>
                <div className="text-gray-600 text-sm lg:text-base">signatures</div>
              </div>
              <div className="text-center sm:text-right">
                <div className="text-sm text-gray-500">Date created</div>
                <div className="text-base lg:text-lg font-semibold">{new Date(petition.created || petition.createdAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</div>
              </div>
            </div>

            {signatureCount >= 10 && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">
                  ✓ This petition has reached 10 signatures and will receive a government response
                </p>
              </div>
            )}

            {signatureCount >= 20 && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800">
                  ✓ This petition has reached 20 signatures and will be considered for debate in Parliament
                </p>
              </div>
            )}

            {petition.status !== 'closed' && (
              <button
                onClick={handleSignature}
                disabled={!user || !isBritishCitizen(user) || isSubmittingSignature}
                className={`w-full py-3 px-4 lg:px-6 rounded-lg font-semibold text-sm lg:text-base ${
                  !user || !isBritishCitizen(user) || isSubmittingSignature
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : hasSignedPetition
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {!user 
                  ? 'Login to sign this petition' 
                  : !isBritishCitizen(user)
                    ? 'Only British Citizens can sign petitions'
                    : isSubmittingSignature 
                      ? (hasSignedPetition ? 'Removing signature...' : 'Signing...') 
                      : hasSignedPetition 
                        ? 'Remove signature' 
                        : 'Sign this petition'
                }
              </button>
            )}

            {!user && petition.status === 'open' && (
              <div className="text-center py-2">
                <p className="text-gray-600 text-sm">You must be logged in and be a British Citizen to sign this petition</p>
              </div>
            )}

            {user && !isBritishCitizen(user) && petition.status === 'open' && (
              <div className="text-center py-2">
                <p className="text-gray-600 text-sm">Only British Citizens can sign petitions</p>
              </div>
            )}

            {petition.status === 'closed' && (
              <div className="text-center py-3">
                <p className="text-gray-600 font-medium">This petition is closed</p>
                <p className="text-gray-500 text-sm">No more signatures can be added</p>
              </div>
            )}
          </div>

          {petition.governmentResponse && (
            <div className="mb-8 p-6 border-l-4 border-blue-500 bg-blue-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <MessageSquare className="w-4 h-4 text-blue-600 mr-2" />
                  <h3 className="text-xl font-semibold text-blue-900">Government Response</h3>
                </div>
              </div>
              <div className="text-blue-800">
                {typeof petition.governmentResponse === 'object' 
                  ? petition.governmentResponse.text || petition.governmentResponse.content || JSON.stringify(petition.governmentResponse) 
                  : petition.governmentResponse
                }
              </div>
              {petition.governmentResponseMeta && (
                <div className="mt-4 text-sm text-blue-700 italic">
                  Added by: <span className="font-medium">{petition.governmentResponseMeta.addedBy}</span> <span className="text-blue-600">({petition.governmentResponseMeta.addedByRole})</span>
                </div>
              )}
            </div>
          )}

          {petition.debateInfo && (
            <div className="mb-8 p-6 border-l-4 border-purple-500 bg-purple-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <MessageSquare className="w-4 h-4 text-purple-600 mr-2" />
                  <h3 className="text-xl font-semibold text-purple-900">Parliament Debate</h3>
                </div>
              </div>
              {typeof petition.debateInfo === 'object' ? (
                <div>
                  {petition.debateInfo.status === 'debated' && (
                    <div className="text-purple-800">
                      <p className="text-lg font-medium mb-3">
                        This petition was debated on {new Date(petition.debateInfo.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long', 
                          year: 'numeric'
                        })}
                      </p>
                      {(petition.debateInfo.hansardLink || petition.debateInfo.hansardUrl) && (
                        <div className="mt-3">
                          <a 
                            href={petition.debateInfo.hansardLink || petition.debateInfo.hansardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
                          >
                            📄 View Official Hansard Record
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {petition.debateInfo.status === 'scheduled' && (
                    <div className="text-purple-800">
                      <p className="text-lg font-medium">
                        This petition is scheduled for debate on {new Date(petition.debateInfo.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                      {(petition.debateInfo.hansardLink || petition.debateInfo.hansardUrl) && (
                        <div className="mt-3">
                          <a 
                            href={petition.debateInfo.hansardLink || petition.debateInfo.hansardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
                          >
                            📄 View Official Hansard Record
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {petition.debateInfo.status === 'cancelled' && (
                    <div className="text-purple-800">
                      <p className="text-lg font-medium">
                        {petition.debateInfo.date ? (
                          <>The debate scheduled for {new Date(petition.debateInfo.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })} has been cancelled</>
                        ) : (
                          'The debate for this petition has been cancelled'
                        )}
                      </p>
                      {(petition.debateInfo.hansardLink || petition.debateInfo.hansardUrl) && (
                        <div className="mt-3">
                          <a 
                            href={petition.debateInfo.hansardLink || petition.debateInfo.hansardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
                          >
                            📄 View Official Hansard Record
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {petition.debateInfo.status === 'not_debated' && (
                    <div className="text-purple-800">
                      <p className="text-lg font-medium">
                        This petition will not be debated in parliament
                      </p>
                      {petition.debateInfo.notDebatedReason && (
                        <div className="mt-3 p-3 bg-purple-100 rounded">
                          <p className="text-purple-700 text-sm">
                            <strong>Reason:</strong> {petition.debateInfo.notDebatedReason}
                          </p>
                        </div>
                      )}
                      {(petition.debateInfo.hansardLink || petition.debateInfo.hansardUrl) && (
                        <div className="mt-3">
                          <a 
                            href={petition.debateInfo.hansardLink || petition.debateInfo.hansardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
                          >
                            📄 View Related Parliamentary Documents
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Added by attribution - matching government response format */}
                  {petition.debateInfoMeta && (
                    <div className="mt-4 text-sm text-purple-700 italic">
                      Added by: <span className="font-medium">{petition.debateInfoMeta.addedBy}</span> <span className="text-purple-600">({petition.debateInfoMeta.addedByRole})</span>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="text-purple-800">
                    {petition.debateInfo}
                  </div>
                  {petition.debateInfoMeta && (
                    <div className="mt-4 text-sm text-purple-700 italic">
                      Added by: <span className="font-medium">{petition.debateInfoMeta.addedBy}</span> <span className="text-purple-600">({petition.debateInfoMeta.addedByRole})</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Signers List */}
        <SignersList signers={signers} signatureCount={signatureCount} />
      </div>
    </div>
  );
};
